import React, { useState } from "react";
import {
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Chip,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  FormHelperText,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CategoryIcon from "@mui/icons-material/Category";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import ImageIcon from "@mui/icons-material/Image";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import ScreenRotationAltIcon from "@mui/icons-material/ScreenRotationAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { VideoSource } from "../../Enums/enums";
import {
  PLATFORM_CATEGORIES,
  PLATFORM_EXTRA_CONFIG_SCHEMA,
  isCategoryMissing,
} from "../../Enums/platform_constants";
import VideoEditDialog from "../../components/VideoEditDialog";
import { asPreselected, savedTemplateIdOf } from "../../components/vreEditSession";
import { hasReviewedEdit } from "../batchEditor/batchItems";
import MRSSThumbnailCreator from "../../components/MRSSThumbnailCreator";
import RawVideoPicker from "../../components/RawVideoPicker";
import DuplicateSelector from "./DuplicateSelector";
import CoverFramePicker from "./CoverFramePicker";

const focusRing = {
  "&:focus-within": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
};

const SOURCE_LABELS = {
  [VideoSource.ORIGINAL]: "Original",
  [VideoSource.LOCAL]: "Local upload",
  [VideoSource.EDITED]: "Edited",
  [VideoSource.DUPLICATE]: "Duplicate",
};

/** Radio "card" option: >=44px target, wraps in a grid, helper text underneath. */
function OptionCard({ value, label, helper, checked }) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio size="small" />}
      label={
        <Box>
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3}>
              {helper}
            </Typography>
          )}
        </Box>
      }
      sx={{
        m: 0,
        minHeight: 44,
        alignItems: "flex-start",
        px: 1,
        py: 0.75,
        border: "1px solid",
        borderColor: checked ? "primary.main" : "divider",
        bgcolor: checked ? "action.selected" : "background.paper",
        borderRadius: 1.5,
        ...focusRing,
      }}
    />
  );
}

export default function PlatformSourceCard({
  platform,
  video,
  config,
  selectedPlatforms,
  platformConfigs,
  brandName,
  getTemplate,
  getTemplateChoices,
  onChange,
}) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [thumbnailCreatorOpen, setThumbnailCreatorOpen] = useState(false);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [selectedRawVideos, setSelectedRawVideos] = useState(null);
  const [expanded, setExpanded] = useState(true); // open by default; the user can collapse it

  // TikTok uses a frame of the video itself as the cover (cover_timestamp in
  // seconds) instead of an uploaded thumbnail image.
  const isTikTok = platform === "TIKTOK";
  const coverTimestamp = config?.extra_config?.cover_timestamp;

  const setCoverTimestamp = (value) => {
    const extraCfg = config?.extra_config || {};
    onChange(platform, { extra_config: { ...extraCfg, cover_timestamp: value } });
  };

  // Extra-config fields for the generic settings panel; TikTok's cover_timestamp
  // is edited in the dedicated Cover Frame section instead.
  const extraSchemaEntries = Object.entries(
    PLATFORM_EXTRA_CONFIG_SCHEMA[platform] || {}
  ).filter(([field]) => !(isTikTok && field === "cover_timestamp"));

  const source = config?.source || VideoSource.ORIGINAL;
  const categoryMissing = isCategoryMissing(platform, config);
  const isResolved =
    (source === VideoSource.ORIGINAL ||
      // A compilation's file already lives on its own MRSSVideo row — no
      // per-platform upload is required for LOCAL to resolve.
      (source === VideoSource.LOCAL && (video?.is_compilation || config?.localVideoPath)) ||
      (source === VideoSource.EDITED && hasReviewedEdit(config)) ||
      (source === VideoSource.DUPLICATE && config?.duplicateOf)) &&
    !categoryMissing;

  // Both orientations: the editor dialog reads the video's frame and opens a
  // landscape video on the horizontal template when one is assigned, anything
  // else on the vertical one.
  const brandTemplate = getTemplate ? getTemplate(platform, "vertical") : null;
  const brandHorizontalTemplate = getTemplate ? getTemplate(platform, "horizontal") : null;
  const preselectedTemplate = asPreselected(brandTemplate);
  const preselectedHorizontalTemplate = asPreselected(brandHorizontalTemplate);
  const templateLabel = (assigned) => assigned.template_name || assigned.template_id;
  // YouTube's second template: the editor gets a Main / Second switch, and a
  // saved edit says which of the two it was made on. Null for every other
  // platform and for a YouTube without a second template.
  const templateChoices = getTemplateChoices ? getTemplateChoices(platform) : null;
  const savedTemplateId = savedTemplateIdOf(config?.renderPayload);
  const usedTemplate =
    templateChoices && savedTemplateId
      ? [templateChoices.vertical.main, templateChoices.vertical.second, templateChoices.horizontal.main, templateChoices.horizontal.second].find(
          (r) => r && String(r.template_id) === savedTemplateId,
        )
      : null;

  // Video URL used by the thumbnail creator — prefer original, fall back to S3
  const videoUrlForThumbnail =
    video?.s3_url || video?.original_video_url || video?.url || "";

  const handleSourceChange = (newSource) => {
    onChange(platform, { source: newSource });
  };

  const handleEditSave = (renderPayload) => {
    // Saving here reviews it too: an Apply-to-all copy becomes a normal edit.
    onChange(platform, { source: VideoSource.EDITED, renderPayload, batchApplied: null });
  };

  // Keep the raw-clip pick on the platform config so Re-edit and draft restore
  // reopen with the same clips selected.
  const handleRawIdsChange = (ids) => {
    if (JSON.stringify(config?.rawVideoIds ?? null) === JSON.stringify(ids)) return;
    onChange(platform, { rawVideoIds: ids });
  };

  const handleLocalFile = (e) => {
    const file = e.target.files[0];
    if (file) onChange(platform, { source: VideoSource.LOCAL, localVideoPath: file.name, localFile: file });
  };

  const handleDuplicateChange = (dup) => {
    onChange(platform, { source: VideoSource.DUPLICATE, duplicateOf: dup });
  };

  const thumbnailSource = config?.thumbnailSource || "none";

  const handleThumbnailSourceChange = (newSource) => {
    onChange(platform, { thumbnailSource: newSource, thumbnailFile: null, thumbnailPreview: null });
  };

  const handleThumbnailFile = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    onChange(platform, { thumbnailSource: "custom", thumbnailFile: file, thumbnailPreview: previewUrl });
  };

  const handleThumbnailFromCreator = (base64) => {
    const dataUrl = base64.startsWith("data:image")
      ? base64
      : `data:image/jpeg;base64,${base64}`;
    // Convert base64 data URL to a File so the existing chunk-upload path in index.js handles it
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const file = new File([u8arr], `thumbnail_${platform.toLowerCase()}.jpg`, { type: mime });
    onChange(platform, { thumbnailSource: "custom", thumbnailFile: file, thumbnailPreview: dataUrl });
  };

  const handleClearThumbnail = () => {
    onChange(platform, { thumbnailSource: "none", thumbnailFile: null, thumbnailPreview: null });
  };

  const isExpanded = expanded;
  const thumbMissingOriginal =
    !(video?.original_thumbnail_url || video?.portal_thumb || video?.thumbnail_url || video?.thumbnail);
  const sourceRadios = [
    !video?.is_compilation && [VideoSource.ORIGINAL, "Original", "Use the uploaded video as is"],
    [VideoSource.LOCAL, "Local upload", "Pick a file from your device"],
    [VideoSource.EDITED, "Edit in Video Editor", "Render a custom edit"],
    [VideoSource.DUPLICATE, "Duplicate", "Reuse another platform's video"],
  ].filter(Boolean);

  return (
    <Paper variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: "hidden", borderLeft: "4px solid", borderLeftColor: isResolved ? "success.main" : "warning.main" }}>
      <Accordion
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        onChange={(e, val) => setExpanded(val)}
        TransitionProps={{ unmountOnExit: false }}
        sx={{ "&:before": { display: "none" }, "@media (prefers-reduced-motion: reduce)": { "& .MuiCollapse-root": { transition: "none" } } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-label={`${platform} settings`}
          sx={{ minHeight: 48, px: { xs: 1.5, sm: 2 }, "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 } }}
        >
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ width: "100%", pr: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>{platform}</Typography>
            <Chip size="small" variant="outlined" label={SOURCE_LABELS[source] || source} />
            {isResolved ? (
              <Chip icon={<CheckCircleIcon />} label="Ready" color="success" size="small" />
            ) : (
              <Chip
                label={
                  categoryMissing
                    ? "Category needed"
                    : source === VideoSource.EDITED && config?.batchApplied
                      ? "Applied — review in batch editor"
                      : "Needs attention"
                }
                size="small"
                color={!categoryMissing && source === VideoSource.EDITED && config?.batchApplied ? "info" : "warning"}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 }, pt: { xs: 0.5, sm: 0.5 } }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.75}>
        Video source
      </Typography>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={source}
          onChange={(e) => handleSourceChange(e.target.value)}
          aria-label={`Video source for ${platform}`}
          sx={{
            display: "grid",
            gap: 1,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          }}
        >
          {/* A compilation has no original_video_url (it's a LOCAL upload) — Original would resolve to an empty URL. */}
          {sourceRadios.map(([val, label, helper]) => (
            <OptionCard key={val} value={val} label={label} helper={helper} checked={source === val} />
          ))}
        </RadioGroup>
      </FormControl>

      {source === VideoSource.LOCAL && (
        <Box mt={1}>
          {video?.is_compilation ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Using compilation video"
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <>
              <Button variant="outlined" component="label" sx={{ minHeight: { xs: 44, sm: 36 }, textTransform: "none", width: { xs: "100%", sm: "auto" }, ...focusRing }}>
                Choose File
                <input type="file" hidden accept="video/*" onChange={handleLocalFile} />
              </Button>
              {config?.localVideoPath && (
                <Typography variant="caption" sx={{ ml: { sm: 1 }, display: { xs: "block", sm: "inline" }, mt: { xs: 0.5, sm: 0 }, wordBreak: "break-all" }}>{config.localVideoPath}</Typography>
              )}
            </>
          )}
        </Box>
      )}

      {source === VideoSource.EDITED && (
        <Box mt={1}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<MovieFilterIcon />}
            onClick={() => setEditDialogOpen(true)}
            sx={{ minHeight: { xs: 44, sm: 36 }, textTransform: "none", ...focusRing }}
          >
            {config?.renderPayload ? "Re-edit" : "Open Editor"}
          </Button>
          {!preselectedTemplate && !preselectedHorizontalTemplate && (
            <Alert severity="info" sx={{ py: 0, px: 1, fontSize: "0.75rem" }}>
              No template assigned for {platform} — template gallery will open.
            </Alert>
          )}
          {usedTemplate && (
            <Chip size="small" label={`Edited · ${templateLabel(usedTemplate)}`} data-testid="used-template" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
          )}
          {(preselectedTemplate || preselectedHorizontalTemplate) && (
            <Typography variant="caption" color="text.secondary">
              {preselectedTemplate && <>Vertical: {templateLabel(brandTemplate)}</>}
              {preselectedTemplate && preselectedHorizontalTemplate && " · "}
              {preselectedHorizontalTemplate && (
                <>Horizontal: {templateLabel(brandHorizontalTemplate)} (for landscape videos)</>
              )}
            </Typography>
          )}
        </Stack>
        {/* Raw clips exist only for WooGlobe WGIDs, not compilations or native videos. */}
        {!video?.is_compilation && !video?.is_native_video && (
          <Box mt={1}>
            <RawVideoPicker
              wgid={video?.unique_key}
              onSelectionChange={setSelectedRawVideos}
              initialSelectedIds={config?.rawVideoIds}
              onIdsChange={handleRawIdsChange}
            />
          </Box>
        )}
        </Box>
      )}

      {(source === VideoSource.ORIGINAL || source === VideoSource.LOCAL) && (
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1.5} sx={{ minHeight: 44 }}>
          <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
            <ScreenRotationAltIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="body2">Convert to Vertical (9:16)</Typography>
              <Typography variant="caption" color="text.secondary">
                Blur background, reformat for social media
              </Typography>
            </Box>
          </Box>
          <Switch
            inputProps={{ "aria-label": `Convert ${platform} video to vertical 9:16` }}
            sx={focusRing}
            checked={!!config?.convertToVertical}
            onChange={(e) => onChange(platform, { convertToVertical: e.target.checked })}
            color="primary"
          />
        </Box>
      )}

      {source === VideoSource.DUPLICATE && (
        <Box mt={1.5} maxWidth={{ xs: "100%", sm: 320 }}>
          <DuplicateSelector
            platform={platform}
            selectedPlatforms={selectedPlatforms}
            platformConfigs={platformConfigs}
            value={config?.duplicateOf}
            onChange={handleDuplicateChange}
          />
          {config?.duplicateOf && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Will reuse {config.duplicateOf} video URL — no separate render.
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {isTikTok ? (
        /* TikTok: cover frame time instead of a thumbnail image */
        <Box>
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Cover Frame
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            TikTok uses a frame of the video as its cover — set the frame time instead of uploading a thumbnail.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
            <TextField
              type="number"
              label="Cover Frame Time"
              value={coverTimestamp ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { setCoverTimestamp(null); return; }
                const n = Math.max(0, Math.floor(Number(raw)));
                setCoverTimestamp(Number.isNaN(n) ? null : n);
              }}
              inputProps={{ min: 0, step: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">sec</InputAdornment>,
              }}
              sx={{ width: { xs: "100%", sm: 190 } }}
            />
            <Tooltip title="Scrub the video and pick the frame time visually">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AccessTimeIcon />}
                  onClick={() => setCoverPickerOpen(true)}
                  disabled={!videoUrlForThumbnail}
                  sx={{ minHeight: { xs: 44, sm: 36 }, textTransform: "none", width: { xs: "100%", sm: "auto" } }}
                >
                  Pick from Video
                </Button>
              </span>
            </Tooltip>
            {coverTimestamp !== null && coverTimestamp !== undefined && coverTimestamp !== "" && (
              <Tooltip title="Clear cover frame time">
                <IconButton aria-label="Clear cover frame time" sx={{ minWidth: { xs: 44, sm: 36 }, minHeight: { xs: 44, sm: 36 }, alignSelf: { xs: "flex-end", sm: "center" } }} onClick={() => setCoverTimestamp(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {source === VideoSource.EDITED && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Editing in the Video Editor? Set the cover from the editor ("Set as Cover") so the
              time matches the final rendered video exactly.
            </Typography>
          )}
        </Box>
      ) : (
      <Box>
        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          <ImageIcon fontSize="small" color="action" />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Thumbnail
          </Typography>
        </Box>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={thumbnailSource}
            onChange={(e) => handleThumbnailSourceChange(e.target.value)}
            aria-label={`Thumbnail for ${platform}`}
            sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}
          >
            <OptionCard value="none" label="None" helper="No thumbnail in the feed" checked={thumbnailSource === "none"} />
            <OptionCard value="original" label="Original" helper="Use the video's own thumbnail" checked={thumbnailSource === "original"} />
            <OptionCard value="custom" label="Custom" helper="Upload or capture a frame" checked={thumbnailSource === "custom"} />
          </RadioGroup>
        </FormControl>

        {thumbnailSource === "none" && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            No thumbnail will be included in the feed for this platform.
          </Typography>
        )}

        {thumbnailSource === "original" && thumbMissingOriginal && (
          <Alert severity="warning" sx={{ mt: 0.5, py: 0.5, px: 1, fontSize: "0.75rem" }}>
            No original thumbnail found for this video — upload a custom thumbnail instead.
          </Alert>
        )}

        {thumbnailSource === "custom" && (
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1} mt={1}>
            <Button variant="outlined" component="label" sx={{ minHeight: { xs: 44, sm: 36 }, textTransform: "none", width: { xs: "100%", sm: "auto" }, ...focusRing }}>
              {config?.thumbnailPreview ? "Change File" : "Choose Image"}
              <input type="file" hidden accept="image/*" onChange={handleThumbnailFile} />
            </Button>

            <Tooltip title="Scrub the video and capture a frame as thumbnail">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => setThumbnailCreatorOpen(true)}
                  disabled={!videoUrlForThumbnail}
                  sx={{ minHeight: { xs: 44, sm: 36 }, textTransform: "none", width: { xs: "100%", sm: "auto" } }}
                >
                  Capture from Video
                </Button>
              </span>
            </Tooltip>

            {config?.thumbnailPreview ? (
              <>
                <Box
                  component="img"
                  src={config.thumbnailPreview}
                  alt="thumbnail preview"
                  sx={{ height: 56, maxWidth: "100%", objectFit: "contain", alignSelf: "flex-start", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                />
                <Tooltip title="Remove thumbnail">
                  <IconButton aria-label="Remove thumbnail" sx={{ minWidth: { xs: 44, sm: 36 }, minHeight: { xs: 44, sm: 36 }, alignSelf: { xs: "flex-end", sm: "center" } }} onClick={handleClearThumbnail}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">No image selected</Typography>
            )}
          </Stack>
        )}
      </Box>
      )}

      {/* Platform Settings — shown for platforms that have categories or extra config.
          TikTok's cover_timestamp is edited in the dedicated Cover Frame section above. */}
      {(PLATFORM_CATEGORIES[platform] || extraSchemaEntries.length > 0) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box>
            <Box display="flex" alignItems="center" gap={0.5} mb={1}>
              <CategoryIcon fontSize="small" color="action" />
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Platform Settings
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} flexWrap="wrap" useFlexGap spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
              {PLATFORM_CATEGORIES[platform] && (
                <FormControl size="small" sx={{ minWidth: { sm: 200 }, maxWidth: { sm: 260 } }} required error={categoryMissing}>
                  <InputLabel id={`cat-label-${platform}`}>Channel / Category</InputLabel>
                  <Select
                    labelId={`cat-label-${platform}`}
                    label="Channel / Category"
                    value={config?.category || ""}
                    onChange={(e) => onChange(platform, { category: e.target.value })}
                  >
                    <MenuItem value="" sx={{ minHeight: 40 }}><em>None</em></MenuItem>
                    {PLATFORM_CATEGORIES[platform].map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ minHeight: 40 }}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {categoryMissing && (
                    <FormHelperText>Select a category to continue</FormHelperText>
                  )}
                </FormControl>
              )}

              {/* Extra config fields (bool toggles, selects, numbers) */}
              {extraSchemaEntries.map(([field, schema]) => {
                const extraCfg = config?.extra_config || {};
                if (schema.type === "bool") {
                  return (
                    <FormControlLabel
                      sx={{ minHeight: 44, m: 0 }}
                      key={field}
                      control={
                        <Switch
                          sx={focusRing}
                          checked={field in extraCfg ? Boolean(extraCfg[field]) : schema.default}
                          onChange={(e) =>
                            onChange(platform, {
                              extra_config: { ...extraCfg, [field]: e.target.checked },
                            })
                          }
                        />
                      }
                      label={<Typography variant="caption">{schema.label}</Typography>}
                    />
                  );
                }
                if (schema.type === "select") {
                  return (
                    <FormControl key={field} size="small" sx={{ minWidth: { sm: 160 } }}>
                      <InputLabel id={`${platform}-${field}-label`}>{schema.label}</InputLabel>
                      <Select
                        labelId={`${platform}-${field}-label`}
                        label={schema.label}
                        value={extraCfg[field] ?? schema.default}
                        onChange={(e) =>
                          onChange(platform, {
                            extra_config: { ...extraCfg, [field]: e.target.value },
                          })
                        }
                      >
                        {schema.options.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                if (schema.type === "number") {
                  return (
                    <TextField
                      key={field}
                      size="small"
                      type="number"
                      label={schema.label}
                      value={extraCfg[field] ?? schema.default ?? ""}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const n = raw === "" ? null : Number(raw);
                        onChange(platform, {
                          extra_config: { ...extraCfg, [field]: n !== null && Number.isNaN(n) ? null : n },
                        });
                      }}
                      inputProps={{ min: schema.min ?? 0, step: 1 }}
                      sx={{ width: { xs: "100%", sm: 190 } }}
                    />
                  );
                }
                return null;
              })}
            </Stack>
          </Box>
        </>
      )}

        </AccordionDetails>
      </Accordion>

      <VideoEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        video={video}
        onSave={handleEditSave}
        onThumbnailSave={handleThumbnailFromCreator}
        preselectedTemplate={preselectedTemplate}
        preselectedHorizontalTemplate={preselectedHorizontalTemplate}
        templateChoices={templateChoices}
        rawVideos={selectedRawVideos}
        savedPayload={config?.renderPayload}
        channelName={`${brandName} - ${platform}`}
        coverTimestamp={isTikTok ? coverTimestamp : undefined}
        onCoverTimestampSave={isTikTok ? setCoverTimestamp : undefined}
      />

      <MRSSThumbnailCreator
        open={thumbnailCreatorOpen}
        setOpen={setThumbnailCreatorOpen}
        url={videoUrlForThumbnail}
        onSave={handleThumbnailFromCreator}
      />

      {isTikTok && (
        <CoverFramePicker
          open={coverPickerOpen}
          onClose={() => setCoverPickerOpen(false)}
          url={videoUrlForThumbnail}
          onSave={setCoverTimestamp}
        />
      )}
    </Paper>
  );
}